import { LitElement, html, nothing, type CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import type { Size } from '../types.js';
import styles from './styles.js';

export type TagSeverity = 'primary' | 'success' | 'info' | 'warning' | 'danger' | 'secondary' | 'contrast';
export type TagVariant  = 'filled' | 'outlined';

/**
 * uwc-tag
 *
 * A small label element for status indicators, category badges, and metadata chips.
 * A feature-rich tag/badge label component.
 *
 * @attr {string}      value    - Tag label. Overridden by the default slot.
 * @attr {TagSeverity} severity - Visual severity: primary | success | info | warning | danger | secondary | contrast.
 * @attr {TagVariant}  variant  - "filled" (default solid bg) or "outlined" (transparent bg with border).
 * @attr {string}      icon     - Bootstrap Icon name rendered before the label.
 * @attr {boolean}     rounded  - Applies a fully-rounded pill shape.
 * @attr {'small'|'medium'|'large'} size - Tag scale (em-based height follows font). Default: medium.
 *
 * @slot (default) - Custom content. When provided, overrides the `value` attribute.
 *
 * @cssprop --uwc-tag-font-size          - Font size. Default: 0.75rem.
 * @cssprop --uwc-tag-font-weight        - Font weight. Default: 700.
 * @cssprop --uwc-tag-radius             - Border radius. Default: 4px (overridden to pill when [rounded]).
 * @cssprop --uwc-tag-primary-bg         - Primary severity background.
 * @cssprop --uwc-tag-primary-color      - Primary severity text color.
 * @cssprop --uwc-tag-success-bg         - Success severity background.
 * @cssprop --uwc-tag-info-bg            - Info severity background.
 * @cssprop --uwc-tag-warning-bg         - Warning severity background.
 * @cssprop --uwc-tag-danger-bg          - Danger severity background.
 * @cssprop --uwc-tag-secondary-bg       - Secondary severity background.
 * @cssprop --uwc-tag-contrast-bg        - Contrast severity background.
 */
export class UwcTag extends LitElement {

  static override styles: CSSResultGroup = styles;

  @property({ reflect: true }) value    = '';
  @property({ reflect: true }) severity: TagSeverity = 'primary';
  @property({ reflect: true }) variant:  TagVariant  = 'filled';
  @property({ reflect: true }) icon     = '';
  @property({ type: Boolean, reflect: true }) rounded = false;
  @property({ reflect: true }) size: Size = 'medium';

  override render() {
    const classes = [
      'uwc-tag',
      this.severity !== 'primary' ? `uwc-tag--${this.severity}` : '',
      this.variant  === 'outlined' ? 'uwc-tag--outlined' : '',
    ].filter(Boolean).join(' ');

    return html`
      <span class="${classes}" part="base" role="status">
        ${this.icon ? html`
          <uwc-icon
            name="${this.icon}"
            size="0.9em"
            class="uwc-tag__icon"
            part="icon"
            aria-hidden="true"
          ></uwc-icon>
        ` : nothing}
        <slot>${this.value}</slot>
      </span>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'uwc-tag': UwcTag; }
}
